<template>
  <v-container mt-7>
    <v-form ref="form" lazy-validation>
      <v-row>
        <v-col class="selectorColumn">
          <v-select
            v-model="orgChoice"
            :items="organizations"
            :menu-props="{ maxHeight: '400' }"
            label="Organizations"
            :disabled="orgDisabled"
            outlined
            dense
            clearable
          >
            <template v-slot:selection="{ item, index }">
              <span>{{ item }}</span>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ orgChoice.length - 1 }}</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="insChoice"
            :items="insurances"
            item-text="Insurance"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Select Insurance"
            :rules="[(insChoice) => insChoice.length !== 0]"
            outlined
            dense
            clearable
            required
            @click:clear="$nextTick(() => (insChoice = []))"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn" v-if="!groupDisabled">
          <v-autocomplete
            v-model="groupChoice"
            :items="groups"
            :menu-props="{ maxHeight: '400' }"
            label="Groups"
            item-text="GROUP_NAME"
            item-value="BILLING_PROVIDER_NPI"
            multiple
            outlined
            dense
            clearable
            :rules="[
              (v) => {
                if (this.user.role === 'Provider' || this.user.role === 'Group') {
                  return groupChoice.length > 0 || 'Please select your group';
                } else {
                  return true;
                }
              },
            ]"
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">{{ item.GROUP_NAME }}</div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ groupChoice.length - 1 }}</span
              >
            </template>
          </v-autocomplete>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="provChoice"
            :items="providers"
            item-value="PROVIDER_ID"
            item-text="PROVIDER_NAME"
            :menu-props="{ maxHeight: '400' }"
            label="Providers"
            multiple
            clearable
            outlined
            dense
            :rules="[
              (v) =>
                (this.user.role === 'Provider' && provChoice.length > 0) ||
                this.user.role !== 'Provider' ||
                'Please select your provider',
            ]"
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">
                {{ item.PROVIDER_NAME }}
              </div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ provChoice.length - 1 }}</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="locChoice"
            :items="locations"
            item-text="PROVIDER_LOCATION"
            item-value="PRACTICE_ID"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Locations"
            multiple
            outlined
            dense
            clearable
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">
                {{ item.PROVIDER_LOCATION || item.SERVICE_LOCATION_NAME || 'No Location Provided' }}
              </div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ locChoice.length - 1 }}</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="reportChoice"
            :items="reports"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Select A Report"
            outlined
            dense
            clearable
            required
            :rules="[(reportChoice) => reportChoice.length !== 0]"
            @click:clear="$nextTick(() => (reportChoice = []))"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="startDate"
                label="Start Date"
                show-current="2018-01"
                append-icon="mdi-calendar"
                outlined
                dense
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              :max="maxEndDate"
              show-current="false"
              type="month"
              @input="menu = false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="selectorColumn">
          <v-menu
            ref="menu2"
            v-model="menu2"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="endDate"
                label="End Date"
                append-icon="mdi-calendar"
                outlined
                dense
                readonly
                v-on="on"
                :disabled="endDateDisabled"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="endDate"
              :max="maxEndDate"
              show-current="false"
              type="month"
              @input="menu2 = false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="selectorColumn">
          <v-btn @click="validate" color="secondary" depressed block class="py-5">
            Run Report
            <v-icon>mdi-play</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-row>
      <v-col v-if="load === true" cols="12" class="text-center">
        <v-progress-circular
          color="secondary"
          indeterminate
          :size="50"
          :width="3"
        ></v-progress-circular>
      </v-col>

      <v-col
        v-else-if="(insChoice.length === 0 || reportChoice.length === 0) && !showReport"
        cols="12"
      >
        <v-alert :value="true" type="info"> Please select all required fields </v-alert>
      </v-col>

      <v-col
        v-else-if="(insChoice.length > 0 || reportChoice.length > 0) && !showReport"
        cols="12"
      >
        <v-alert :value="true" type="info"> Press run report to continue </v-alert>
      </v-col>

      <v-col
        v-else-if="
          reportChoice === 'Quality Scorecard' &&
          qualityScorecardData.length === 0 &&
          showReport &&
          !load &&
          !changeDetected
        "
        cols="12"
      >
        <v-alert type="info">
          There is no data available for this selection. Please change your filter
          selections and run report again
        </v-alert>
      </v-col>

      <v-col
        v-else-if="
          reportChoice === 'Open Quality Gaps' &&
          openQualityGapsData.length === 0 &&
          showReport &&
          !load &&
          !changeDetected
        "
        cols="12"
      >
        <v-alert type="info">
          There is no data available for this selection. Please change your filter
          selections and run report again
        </v-alert>
      </v-col>

      <v-col v-else-if="showReport && changeDetected" cols="12">
        <v-alert :value="true" type="warning" dense>
          Change detected: Run the report to see updated data
        </v-alert>
      </v-col>
      <v-card>
        <QualityScorecard
          v-if="
            showReport === 'Quality Scorecard' && qualityScorecardData.length > 0 && !load
          "
          :organization="orgChoice"
          :groups="groupNum"
          :providers="provChoice"
          :locations="locChoice"
          :startDate="startDate"
          :endDate="endDate"
          :reportData="qualityScorecardData"
        />

        <QualityGaps
          v-if="
            showReport === 'Open Quality Gaps' && openQualityGapsData.length > 0 && !load
          "
          :organization="orgChoice"
          :groups="groupNum"
          :providers="provChoice"
          :locations="locChoice"
          :insurance="insChoice"
          :startDate="startDate"
          :endDate="endDate"
          :reportData="openQualityGapsData"
        />
      </v-card>
    </v-row>
  </v-container>
</template>
<script>
import axios from 'axios';
import QualityGaps from '../components/qualityComponents/qualityGaps.vue';
import QualityScorecard from '../components/qualityComponents/qualityScorecard.vue';

export default {
  components: {
    QualityGaps,
    QualityScorecard,
  },
  data() {
    return {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 3)
        .toISOString()
        .substr(0, 7),
      endDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1)
        .toISOString()
        .substr(0, 7),
      maxEndDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1)
        .toISOString()
        .substr(0, 7),
      menu: false,
      modal: false,
      menu2: false,
      user: '',
      insurance: [],
      orgChoice: [],
      groupChoice: [],
      provChoice: [],
      locChoice: [],
      insChoice: [],
      reportChoice: [],
      qualityScorecardData: [],
      openQualityGapsData: [],
      showReport: false,
      reports: ['Quality Scorecard', 'Open Quality Gaps'],
      organizations: [],
      providers: [],
      groups: [],
      groupObject: [],
      groupNum: [],
      insurances: [],
      locations: [],
      valid: false,
      load: false,
    };
  },
  mounted() {
    this.getUser();
  },
  methods: {
    async getData() {
      this.load = true;
      const { orgChoice } = this;
      const { insChoice } = this;
      const { groupNum } = this;
      const providers = this.provChoice;
      const { locChoice } = this;
      const { startDate } = this;
      const { endDate } = this;
      const { reportChoice } = this;

      // let providers = [];
      // if (provChoice.length > 0) {
      //   provChoice.forEach((prov) => {
      //     if (providers.indexOf(prov.Center_PCP_Number) === -1) {
      //       providers.push(prov.Center_PCP_Number);
      //     }
      //   });
      // } else {
      //   providers = [];
      // }

      let locations = [];
      if (locChoice.length > 0) {
        locChoice.forEach((loca) => {
          if (locations.indexOf(loca.SERVICE_LOCATION_NAME) === -1) {
            locations.push(loca.SERVICE_LOCATION_NAME);
          }
        });
      } else {
        locations = [];
      }

      // This will grab the correct groupNums if no groups are selected. This is for a group level login when only select groups available to the user
      if (this.user.role === 'Group' && this.groupNum.length === 0) {
        this.cancelSource = axios.CancelToken.source();
        await axios
          .get(`api/selection/${this.insChoice}/group/${this.groups}`, {
            cancelToken: this.cancelSource.token,
          })
          .then((response) => {
            this.cancelSource = null;
            const { data } = response;
            data.forEach((element) => {
              if (this.insChoice) {
                if (this.groupNum.indexOf(element.SERVICE_LOCATION_NAME) === -1) {
                  this.groupNum.push(element.SERVICE_LOCATION_NAME);
                }
              }
            });
          });
      }

      this.changeDetected = false;
      this.showReport = reportChoice;
      let url;
      if (reportChoice === 'Quality Scorecard') {
        url = '/api/quality/scorecard/demo/';
      } else if (reportChoice === 'Open Quality Gaps') {
        url = `api/quality/qualitygaps/orgs=${orgChoice}/groups=${groupNum}/providers=${providers}/locs=${locations}/${insChoice}/${startDate}/${endDate}`;
      }

      // Check for pending request and if so cancel old one for new incoming

      axios.get(url).then((response) => {
        if (reportChoice === 'Quality Scorecard') {
          this.qualityScorecardData = response.data;
        } else if (reportChoice === 'Open Quality Gaps') {
          this.openQualityGapsData = response.data;
        }
        this.load = false;
      });
    },
    async checkPresets() {
      if (
        this.user.role === 'Clinical'
        || this.user.role === 'Provider'
        || this.user.role === 'Group'
        || this.user.role === 'Affiliation'
      ) {
        this.setPresets();
      } else {
        this.getOrgs();
        this.getInsurances();
      }
    },
    async setPresets() {
      this.organizations = this.user.organization;
      this.groups = this.user.group;
      this.insurances = this.user.insurance;

      if (this.user.role === 'Provider' || this.user.role === 'Specialist') {
        const { insurance, organization } = this.user;
        this.insChoice = insurance[0];
        this.orgChoice = organization;
        this.groupChoice = this.user.group;
        this.getProvider(this.user.npi);
        this.location = this.user.location;
      } else if (this.user.role === 'Group' || this.user.role === 'Affiliation') {
        this.orgChoice = this.user.organization;
        this.insChoice = this.user.insurance[0];
      }
    },
    async getUser() {
      await axios
        .get('/api/user')
        .then((response) => {
          this.user = response.data[0];
        })
        .catch(err => this.$router.push('/login'));
      this.checkPresets();
    },
    async getOrgs() {
      axios.get('api/list/organization').then((response) => {
        this.organizations = response.data;
        this.orgChoice = this.organizations[0];
      });
    },
    async getLocations() {
      await axios.get(`api/selection/${this.provChoice}/locations`).then((response) => {
        this.locations = (response.data || []).map((loc) => ({
          ...loc,
          PROVIDER_LOCATION: loc.PROVIDER_LOCATION != null ? loc.PROVIDER_LOCATION : 'No Location Provided'
        }));
      });
    },
    async getProviders() {
      await axios.get(`api/selection/${this.groupChoice}/providers`).then((response) => {
        this.providers = response.data;
      });
    },
    async getProvider(npi) {
      await axios.get(`api/selection/single/${npi}/provider`).then((response) => {
        this.providers = response.data;
        console.log(this.providers);
      });
    },
    async getInsurances() {
      await axios.get('api/list/insurances').then((response) => {
        // filter to only return insurances with data
        this.insurances = response.data;
      });
    },
    async getGroups() {
      // Assigns entire user.group array if only one insurance, the else if is there in case there is more than one insurance and can only access certain groups within each insurance
      if (this.user.role === 'Group' && this.insurances.length === 1) {
        await axios.get(`api/list/npi/${this.user.npi}/groups`).then((response) => {
          this.groups = response.data;
        });
      } else if (this.user.role === 'Affiliation') {
        await axios.get(`api/list/ipaId/${this.user.npi}/groups`).then((response) => {
          this.groups = response.data;
        });
      } else if (this.user.role === 'Group' && this.insurances.length > 1) {
        await axios.get(`api/list/${this.insChoice}/groups`).then((response) => {
          const userObjGroups = this.user.group;
          const allPossibleGroups = response.data;
          this.groups = [];
          allPossibleGroups.forEach((grp) => {
            if (userObjGroups.indexOf(grp) !== -1) {
              this.groups.push(grp);
            }
          });
        });
      } else if (this.insChoice.length > 1) {
        await axios.get(`api/list/${this.insChoice}/groups`).then((response) => {
          this.groups = response.data;
        });
      }
    },
    cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel('Start new search, stop active search');
      }
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.getData();
      }
    },
  },
  watch: {
    async groupChoice() {
      this.changeDetected = true;

      if (this.groupChoice) {
        this.getProviders();
      }

      // Populates the group selector for group level logins with one insurance and one group available
      // if (this.user.role === 'Group' && this.insurances.length === 1 && this.groups.length === 1) {
      //   this.groupChoice = this.user.group;
      // }

      // if (this.groupChoice.length > 0) {
      //   this.providers = [];
      //   this.provChoice = [];
      //   this.locations = [];
      //   this.locChoice = [];
      //   this.groupNum = [];
      //   // Check for pending request and if so cancel old one for new incoming
      //   this.cancelSearch();
      //   // Set the CancelToken
      //   this.cancelSource = axios.CancelToken.source();
      //   await axios
      //     .get(`api/selection/${this.insChoice}/group/${this.groupChoice}`, {
      //       cancelToken: this.cancelSource.token,
      //     })
      //     .then((response) => {
      //       // Set cancelsource back to null
      //       this.cancelSource = null;
      //       const { data } = response;
      //       this.groupObject = data;
      //       data.forEach((element) => {
      //         if (this.user.role === 'Provider' || this.user.role === 'Clinical') {
      //           if (this.user.provider.includes(element.Provide_Name)) {
      //             this.providers.push(element);
      //             this.provChoice.push(element);
      //           }
      //         } else {
      //           this.providers.push(element);
      //         }
      //         if (this.insChoice) {
      //           if (this.groupNum.indexOf(element.Center_PCP_Number) === -1) {
      //             this.groupNum.push(element.Center_PCP_Number);
      //           }
      //         }
      //       });
      //     });
      // } else {
      //   this.providers = [];
      //   this.provChoice = [];
      //   this.locChoice = [];
      //   this.locations = [];
      //   this.groupNum = [];
      // }
    },
    async provChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }

      if (this.provChoice) {
        this.getLocations();
      }
      // if (this.provChoice !== []) {
      //   const prov = this.provChoice;
      //   let allProviders = this.providers;
      //   let providerNames = [];
      //   this.locations = [];
      //   this.locChoice = [];

      //   // Create an Array of selected provider names
      //   prov.forEach((provs) => {
      //     providerNames.push(provs.Provide_Name);
      //   });
      //   // Loop through all the providers objects and see if there is a match with any names in providerNames array
      //   allProviders.forEach((p) => {
      //     if (providerNames.indexOf(p.Provide_Name) !== -1) {
      //       // Add the location
      //       if (p.Center_Location === null) {
      //         this.locations.push(`${p.Center_PCP_Number} - No Location Provided`);
      //       } else {
      //         // Add the location and add PCP_IDs, there can be multiple for a single provider
      //         this.locations.push(p.Center_Location);
      //       }
      //     }
      //   });
      // } else {
      //   this.locChoice = [];
      //   this.locations = [];
      // }
    },
    async insChoice() {
      this.changeDetected = true;
      this.groupChoice = [];
      this.provChoice = [];
      this.locChoice = [];
      if (
        this.user.role === 'Admin'
        || this.user.role === 'Superuser'
        || this.user.role === 'Group'
        || this.user.role === 'Affiliation'
      ) {
        if (this.insChoice !== []) {
          this.getGroups();
        }
      } else {
        this.insurances = this.user.insurance;
        if (this.user.role === 'Provider' || this.user.role === 'Clinical') {
          this.groupChoice = this.user.group;
        }
      }
    },
    reportChoice() {
      this.changeDetected = true;
    },
  },
  computed: {
    orgDisabled() {
      // if (
      //   this.user.role === 'Provider' ||
      //   this.user.role === 'Group' ||
      //   this.user.role === 'Clinical'
      // ) {
      //   return true;
      // }
      return false;
    },
    insDisabled() {
      // if (
      //   this.user.role === 'Provider' ||
      //   this.user.role === 'Clinical' ||
      //   this.insurances.length == 1
      // ) {
      //   return true;
      // }
      return false;
    },
    provDisabled() {
      // if (this.groupChoice.length === 0) {
      //   this.provChoice = [];
      //   return true;
      // }
      // if (this.user.role === 'Provider' || this.user.role === 'Clinical') {
      //   return true;
      // }

      return false;
    },
    locDisabled() {
      if (this.provChoice.length === 0 || this.groupChoice.length === 0) {
        this.locChoice = [];
        return true;
      }
      return false;
    },
    groupDisabled() {
      if (this.orgChoice.length === 0 || this.insChoice.length === 0) {
        this.groupChoice = [];
        return true;
      }
      if (this.user.role === 'Clinical' || this.user.role === 'Provider') {
        return true;
      }
      // if (this.user.role === 'Group' && this.groups.length === 1) {
      //   this.groupChoice = this.user.group;

      //   return true;
      // }
      return false;
    },
    endDateDisabled() {
      this.endDate = this.startDate;

      return true;
    },
  },
};
</script>
<style scoped>
.newSelect .v-select__selection {
  white-space: nowrap;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selection-text {
  margin-right: 5px;
}
@media only screen and (min-width: 1200px) {
  .selection-text {
    white-space: nowrap;
    overflow: hidden;
    width: 70%;
  }
}
</style>
